/**
 * client/src/characters/Unattributed.tsx — the lines nobody claimed, reported rather than hidden.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THIS PANEL IS THE HONEST HALF OF THE FEATURE, AND IT IS NOT A TODO LIST.
 *
 * Attribution is a guess with a stated accuracy. The explicit-tag rules resolve `“…,” said Holmes`
 * and nothing else — roughly half of Alice's dialogue, close to none of the Gita's, where speech is a
 * `Krishna said:` heading or is unmarked entirely. A screen that showed only the characters it DID
 * find would look complete on a book it had barely read, and the way anybody would discover that is
 * by hearing the wrong voice.
 *
 * So the number is on the screen, per chapter, biggest gap first — and it is one press to give a
 * whole chapter of it to somebody. On the Gita that is not a workaround, it is the fastest correct
 * path: two speakers, a handful of chapters, five minutes of a human's attention, and a book in two
 * voices that no model was involved in.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **No new CSS.** Every class here already exists in `client/src/ui/app.css`.
 */

import { useCallback, useState, type ReactElement } from "react";

import { ApiError, api } from "../lib/api.ts";
import type { CastQuote, CastScreen } from "./wire.ts";

interface AssignAnswer {
  applied: boolean;
  lines: number;
  quote: CastQuote;
}

export function Unattributed({
  bookId,
  screen,
  onChanged,
}: {
  bookId: string;
  screen: CastScreen;
  onChanged: (next: CastScreen) => void;
}): ReactElement | null {
  const [characterId, setCharacterId] = useState("");
  const [chapterOrd, setChapterOrd] = useState<string>("");
  const [quote, setQuote] = useState<CastQuote | null>(null);
  const [lines, setLines] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { sentences, dialogue, unattributed } = screen.dialogue;
  const url = `/books/${encodeURIComponent(bookId)}/characters/assign`;

  const body = useCallback(
    (apply: boolean) => ({
      characterId,
      chapterOrd: chapterOrd === "" ? null : Number(chapterOrd),
      apply,
    }),
    [characterId, chapterOrd],
  );

  const price = useCallback(async () => {
    if (characterId === "") return;
    setBusy(true);
    setError(null);
    try {
      const got = await api.post<AssignAnswer>(url, body(false));
      setQuote(got.quote);
      setLines(got.lines);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't work out what that would cost.");
    } finally {
      setBusy(false);
    }
  }, [body, characterId, url]);

  const apply = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const got = await api.post<AssignAnswer & CastScreen>(url, body(true));
      onChanged(got);
      setQuote(null);
      setLines(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't assign those lines.");
    } finally {
      setBusy(false);
    }
  }, [body, onChanged, url]);

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Lines nobody is named in</h2>

      {/* The honest denominator, in one sentence. "2 190 of 9 314 sentences are dialogue" is a fact
          about the BOOK; the unattributed count is a fact about how well the guesser did on it, and
          keeping them in one sentence is what stops the second being read as the first. */}
      <p className="ab-lede">
        <span className="ab-num">{dialogue.toLocaleString("en-GB")}</span> of{" "}
        <span className="ab-num">{sentences.toLocaleString("en-GB")}</span> sentences in this book are inside quotation marks.{" "}
        {unattributed === 0 ? (
          <>Every one of them is assigned to somebody.</>
        ) : (
          <>
            <span className="ab-num">{unattributed.toLocaleString("en-GB")}</span> of those name nobody — <em>“Run!” she cried</em>,
            or a speech with no tag at all.
          </>
        )}
      </p>

      {unattributed === 0 ? null : (
        <>
          <p className="ab-hint ab-hint-quiet">
            Those lines are read by the narrator, which is a correct answer and often the right one. Assigning a chapter of them
            takes one press, and nothing is queued when you do.
          </p>

          {screen.unattributedByChapter.length > 0 && (
            <ul className="ab-runners">
              {screen.unattributedByChapter.slice(0, 8).map((c) => (
                <li key={c.chapterOrd} className="ab-runner-title">
                  <button
                    type="button"
                    className="ab-link"
                    onClick={() => {
                      setChapterOrd(String(c.chapterOrd));
                      setQuote(null);
                    }}
                  >
                    Chapter {c.chapterOrd}
                  </button>{" "}
                  <span className="ab-runner-score">{c.lines.toLocaleString("en-GB")} lines</span>
                </li>
              ))}
            </ul>
          )}

          <div className="ab-fields">
            <div className="ab-field">
              <label className="ab-eyebrow" htmlFor="assign-who">
                Assign them to
              </label>
              <select
                id="assign-who"
                className="ab-select"
                value={characterId}
                disabled={busy}
                onChange={(e) => {
                  setCharacterId(e.target.value);
                  setQuote(null);
                }}
              >
                <option value="">choose somebody…</option>
                {screen.characters.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.voiceLabel === null ? "" : ` — ${c.voiceLabel}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="ab-field">
              <label className="ab-eyebrow" htmlFor="assign-where">
                In
              </label>
              <select
                id="assign-where"
                className="ab-select"
                value={chapterOrd}
                disabled={busy}
                onChange={(e) => {
                  setChapterOrd(e.target.value);
                  setQuote(null);
                }}
              >
                <option value="">the whole book</option>
                {screen.unattributedByChapter.map((c) => (
                  <option key={c.chapterOrd} value={String(c.chapterOrd)}>
                    chapter {c.chapterOrd} ({c.lines} lines)
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error !== null && <p className="ab-hint ab-hint-bad">{error}</p>}

          <div className="ab-actions">
            <button type="button" className="ab-btn ab-btn-ghost" disabled={busy || characterId === ""} onClick={() => void price()}>
              What would that cost?
            </button>
            {quote !== null && (
              <button type="button" className="ab-btn ab-btn-primary" disabled={busy} onClick={() => void apply()}>
                Assign {(lines ?? 0).toLocaleString("en-GB")} lines
              </button>
            )}
          </div>

          {/* VERBATIM, and usually "nothing to make": assigning lines to a character who is read by
              the narrator changes no voice and therefore costs no GPU. Saying so is what stops
              somebody treating a free correction as an expensive one and not making it. */}
          {quote !== null && <p className="ab-note">{quote.summary}</p>}
        </>
      )}
    </section>
  );
}
